import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { SessionService } from '@app/services/session/session.service';
import { isCertValid } from '@app/store/selectors/session.selector';
import { GlobalState } from '@app/store/store';
import { Button } from '@infocert/ng-ui-kit/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { LocalStorageService } from 'ngx-webstorage';
import { lastValueFrom, Observable, tap } from 'rxjs';
import {
  RenewalData,
  UserCertificate,
} from './../../../store/models/certificate.model';
import { Migration, UserSession } from './../../../store/models/session.model';

@UntilDestroy()
@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
  standalone: false,
})
export class HomePageComponent implements OnInit, OnDestroy {
  constructor(
    private sessionService: SessionService,
    private certificateService: CertificateService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private store: Store<GlobalState>,
    private _translateService: TranslateService
  ) {}

  private certificateNavTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly certificateNavDelayMs = 300;

  session: UserSession | undefined;
  certificate: UserCertificate | undefined;

  automatic: boolean;
  isEseal: boolean = false;
  isIcssUser: boolean;
  isTrissUser: boolean;
  isIsacUser: boolean;
  certificateStatus: string;
  renewalData: RenewalData;
  otpType: string;
  isExpirationToast: boolean;
  expirationDate: string = '';

  renewable: boolean = false;
  suspendable: boolean;
  isTrissTokenHardware: boolean = false;

  isPkBox: boolean = false;
  canUseLongTermToken: boolean;
  renewalTrissForAutomaticOrEseal: boolean;

  isCertValid$: Observable<boolean>;

  statusModalList: string[] = [
    'SUSPENDED',
    'REVOKED',
    'INVALID',
    'NOT_VALID',
    'EXPIRED',
  ];

  async ngOnInit(): Promise<void> {
    console.log('🚀 ~ HomePageComponent init');

    if (this.localStorageService.retrieve('refreshData')) {
      location.reload();
      this.localStorageService.clear('refreshData');
      this.refreshData();
    }

    this.store
      .pipe(
        untilDestroyed(this),
        tap(async store => {
          if (store.session) {
            this.session = store.session;
            this.automatic = store.session.automatic;
            this.isIcssUser = store.session.icssUser;
            this.isTrissUser = store.session.trissUser;
            this.isIsacUser = store.session.isacUser;
            this.isEseal = store.session.eseal;
          }
          if (store.certificate) {
            this.certificate = store.certificate;
            this.certificateStatus = store.certificate.certificateStatus;
            this.expirationDate = moment(
              store.certificate.validityTo.substring(0, 10)
            ).format('DD/MM/YYYY');
            // bug fix per modale che non appare dopo refresh
            if (this.certificateStatus !== null) {
              // se esiste nella lista chiama il metodo
              this.statusModalList.includes(this.certificateStatus) &&
                this.showModal();
            }

            if (this.isIcssUser) {
              this.renewalData = await lastValueFrom(
                this.certificateService.getRenewalState()
              );
            }

            //if show renewable box
            if (this.isIcssUser) {
              this.renewable =
                store.certificate.renewable &&
                !this.localStorageService.retrieve('isSubCAPeru');
            } else {
              this.renewable =
                store.certificate.renewable &&
                !this.localStorageService.retrieve('isSubCAPeru');
            }

            this.suspendable =
              !this.localStorageService.retrieve('isSubCAPeru');

            //if show otp box
            this.otpType =
              this.localStorageService.retrieve('certificate')?.otpType;
            if (this.isTrissUser && this.otpType === 'EXTERNAL') {
              this.isTrissTokenHardware = true;
            }
          }

          if (store.session && store.certificate) {
            this.isPkBox = !this.isIcssUser && !this.isTrissUser;
            this.canUseLongTermToken =
              (this.automatic || this.isEseal) &&
              (this.isIcssUser || this.isTrissUser);
          }

          //non mostrare card rinnovo per rinnovi triss on triss automatic o easel o diversi da pkbox
          this.renewalTrissForAutomaticOrEseal =
            this.isEseal ||
            (this.automatic && (this.isTrissUser || !this.isPkBox));
        })
      )
      .subscribe();
    // Calcolo se la data attuale è entro 89 giorni dalla scadenza
    const expiration = moment(this.certificate.validityTo.substring(0, 10));
    const today = moment().startOf('day');
    const daysDiff = expiration.diff(today, 'days');

    this.isExpirationToast = daysDiff <= 89 && daysDiff >= 0;
    //console.log('expiration', daysDiff);
    this.isCertValid$ = this.store.select(isCertValid);
  }

  // funzione chiamato solo una volta dopo aver inizializzato tutta la view del componente (non viene richiamato con il refresh)
  ngAfterViewInit() {
    // se esiste nella lista chiama il metodo
    this.statusModalList.includes(this.certificateStatus) && this.showModal();
  }

  ngOnDestroy(): void {
    if (this.certificateNavTimeout) {
      clearTimeout(this.certificateNavTimeout);
      this.certificateNavTimeout = null;
    }
  }

  refreshData() {
    this.sessionService.getUserSession().then(async session => {
      const certificates = await lastValueFrom(
        this.certificateService.getCertificateList()
      );
      this.certificateService.certID = certificates?.[0].certId;
      const certificate = await lastValueFrom(
        this.certificateService.getCertificate()
      );
    });
  }

  onClick(path: string) {
    if (path === 'cert') {
      if (this.certificateNavTimeout) {
        clearTimeout(this.certificateNavTimeout);
      }
      this.certificateNavTimeout = setTimeout(() => {
        this.router.navigate([path]);
        this.certificateNavTimeout = null;
      }, this.certificateNavDelayMs);
      return;
    }

    this.router.navigate([path]);
  }

  openSupport() {
    window.open(
      this._translateService.instant('URLS.CONTACT_SUPPORT_URL'),
      '_blank'
    );
  }

  public statusModalVisible = false;

  showModal() {
    this.statusModalVisible = true;
  }

  onToggleStatusModal(visible: boolean): void {
    this.statusModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  closeModal() {
    // force logout for status modal if modal closed
    this.sessionService.logout();
    this.statusModalVisible = false;
  }

  get showMigrationBanner(): boolean {
    return (
      this.session?.migrationStatus === Migration.ACCEPTED ||
      this.session?.migrationStatus === Migration.AVAILABLE ||
      this.session?.migrationStatus === Migration.TO_FINALIZE
    );
  }

  get action(): Button {
    const renewLabel = this._translateService.instant(
      'MULTI_CERT_PAGE.STATUS.RENEW'
    );
    return {
      label: renewLabel,
      altText: renewLabel,
      onClick: () => this.router.navigate(['/renew']),
    };
  }
}
